import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteConfigProvider } from './contexts/SiteConfigContext';
import { isSubdomainAccess } from './services/subdomain';
import CustomerLogin from './components/auth/CustomerLogin';
import OrderCart from './components/customer/OrderCart';
import FinancialReports from './components/customer/FinancialReports';
import OrderHistory from './components/customer/OrderHistory';
import OrderDetails from './components/customer/OrderDetails';
import EditOrder from './components/customer/EditOrder';
import RecurringOrders from './components/customer/RecurringOrders';
import ProductCatalog from './components/customer/ProductCatalog';
import AdminSEO from './components/admin/AdminSEO';
import AdminActivityLogs from './components/admin/AdminActivityLogs';
import AdminSecurityEvents from './components/admin/AdminSecurityEvents';
import AdminSupportTickets from './components/admin/AdminSupportTickets';
import AdminTicketDetail from './components/admin/AdminTicketDetail';
import CompanyCustomers from './components/company/CompanyCustomers';
import CustomerDetail from './components/company/CustomerDetail';
import CompanyOrders from './components/company/CompanyOrders';
import CompanySettings from './components/company/CompanySettings';
import CompanyProducts from './components/company/CompanyProducts';
import TomorrowsBake from './components/company/TomorrowsBake';
import TodaysDelivery from './components/company/TodaysDelivery';
import SupportTickets from './components/company/SupportTickets';
import TicketDetail from './components/company/TicketDetail';
import NewTicket from './components/company/NewTicket';
import CompanyIngredients from './components/company/CompanyIngredients';
import CompanySuppliers from './components/company/CompanySuppliers';
import CompanyInventory from './components/company/CompanyInventory';
import CompanyPurchaseOrders from './components/company/CompanyPurchaseOrders';
import CompanyStarters from './components/company/CompanyStarters';
import CompanyRecipes from './components/company/CompanyRecipes';
import CompanyBatchOptimization from './components/company/CompanyBatchOptimization';
import CostCalculator from './components/company/CostCalculator';
import WebstoreSettings from './components/company/WebstoreSettings';
import WebstoreCustomers from './components/company/WebstoreCustomers';
import WebstoreOrders from './components/company/WebstoreOrders';
import WebstoreCoupons from './components/company/WebstoreCoupons';
import WebstoreMessages from './components/company/WebstoreMessages';
import BakersCalculator from './components/company/BakersCalculator';
import CompanyProductions from './components/company/CompanyProductions';
import PublicStore from './components/store/PublicStore';
import CustomerSupportTickets from './components/customer/CustomerSupportTickets';
import CustomerNewTicket from './components/customer/CustomerNewTicket';
import CustomerTicketDetail from './components/customer/CustomerTicketDetail';
import CustomerSettings from './components/customer/CustomerSettings';
import Homepage from './components/Homepage';
import Register from './components/Register';
import Pricing from './components/Pricing';
import CheckoutSuccess from './components/CheckoutSuccess';
import StaticPage from './components/StaticPage';
import ContactPage from './components/ContactPage';
import HelpCenter from './components/HelpCenter';
import UserGuide from './components/UserGuide';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import EmailTemplates from './components/admin/EmailTemplates';
import AdminHelpCenter from './components/admin/AdminHelpCenter';
import AdminUserGuide from './components/admin/AdminUserGuide';
import RouteTracker from './components/common/RouteTracker';
import ScrollToTop from './components/common/ScrollToTop';
import NotFound from './components/common/NotFound';
import CookieConsent, { initAnalyticsFromConsent } from './components/common/CookieConsent';
import MaintenancePage from './components/common/MaintenancePage';
import { useSiteConfig } from './contexts/SiteConfigContext';
import siteConfigService from './services/siteConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const GeoBlockScreen: React.FC = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', margin: 0, background: '#f8fafc', color: '#334155',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  }}>
    <div style={{ textAlign: 'center', maxWidth: 480, padding: '40px 24px' }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: 12 }}>This service is currently available in the United States only.</h1>
      <p style={{ color: '#64748b', lineHeight: 1.6 }}>We appreciate your interest and hope to expand to your region in the future.</p>
    </div>
  </div>
);

// Lazy-load heavy dashboard components (chart.js)
const CustomerDashboard = React.lazy(() => import('./components/customer/CustomerDashboard'));
const CompanyDashboard = React.lazy(() => import('./components/company/CompanyDashboard'));

// Initialize analytics from previously saved consent on app boot (after config loads GA ID)
siteConfigService.getConfig().then(() => initAnalyticsFromConsent());

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string; permKey?: string }> = ({ 
  children, 
  requiredRole,
  permKey,
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const onSubdomain = isSubdomainAccess();

  // Show loading spinner while authentication is being validated
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/customer-login" replace />;
  }

  // On a subdomain, block admin routes entirely
  if (onSubdomain && requiredRole === 'admin') {
    if (user?.role === 'company') return <Navigate to="/company/dashboard" replace />;
    return <Navigate to="/customer-dashboard" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Redirect to the appropriate dashboard based on role
    if (user?.role === 'company') return <Navigate to="/company/dashboard" replace />;
    if (user?.role === 'admin') return <Navigate to="/manage/settings" replace />;
    return <Navigate to="/customer-dashboard" replace />;
  }

  // Staff permission check: sub-admins without the required permission go to dashboard
  if (permKey && user?.is_company_admin && !user?.is_company_owner) {
    const perms: Record<string, boolean> = (user?.permissions as Record<string, boolean>) || {};
    if (!perms[permKey]) {
      return <Navigate to="/company/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

/** Block non-admin users when maintenance mode is active. */
const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = useSiteConfig();
  const { user, isAuthenticated } = useAuth();

  if (config?.maintenance_mode && !(isAuthenticated && user?.role === 'admin')) {
    return <MaintenancePage message={config.maintenance_message} />;
  }

  return <>{children}</>;
};

function App() {
  const [geoBlocked, setGeoBlocked] = React.useState(false);
  const [geoChecked, setGeoChecked] = React.useState(false);

  React.useEffect(() => {
    const checkGeo = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://127.0.0.1:8001/api' : '/api');
        const res = await fetch(`${baseUrl}/geo-check/`);
        if (res.status === 403) {
          setGeoBlocked(true);
        }
      } catch {
        // Network error — allow through (fail-open)
      } finally {
        setGeoChecked(true);
      }
    };
    checkGeo();
  }, []);

  if (!geoChecked) return null;
  if (geoBlocked) return <GeoBlockScreen />;

  return (
    <SiteConfigProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <RouteTracker />
          <MaintenanceGate>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={
                (() => {
                  const h = window.location.hostname;
                  const isCustom = h !== 'sourdoughsy.com' && !h.endsWith('.sourdoughsy.com') && h !== 'localhost' && h !== '127.0.0.1';
                  if (isCustom) return <Navigate to="/store" replace />;
                  return isSubdomainAccess() ? <Navigate to="/customer-login" replace /> : <Homepage />;
                })()
              } />
              <Route path="/register" element={<Register />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/page/:slug" element={<StaticPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/docs" element={<UserGuide />} />
              <Route path="/customer-login" element={<CustomerLogin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />

            {/* Customer protected routes */}
            <Route 
              path="/customer-dashboard" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" /></div>}>
                    <CustomerDashboard />
                  </Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/products" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <ProductCatalog />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/cart" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <OrderCart />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orders" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <OrderHistory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orders/:id" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <OrderDetails />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orders/:id/edit" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <EditOrder />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/recurring-orders" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <RecurringOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <FinancialReports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/support" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerSupportTickets />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/support/new" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerNewTicket />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/support/:id" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerTicketDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/settings" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSEO />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/activity-logs" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminActivityLogs />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/security-events" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSecurityEvents />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/support" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminSupportTickets />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/support/:id" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminTicketDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/email-templates" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <EmailTemplates />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/help-center" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminHelpCenter />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage/user-guide" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminUserGuide />
                </ProtectedRoute>
              } 
            />

            {/* Company protected routes */}
            <Route 
              path="/company/dashboard" 
              element={
                <ProtectedRoute requiredRole="company">
                  <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" /></div>}>
                    <CompanyDashboard />
                  </Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/customers" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_customers">
                  <CompanyCustomers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/customers/:id" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_customers">
                  <CustomerDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/orders" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_orders">
                  <CompanyOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/settings" 
              element={
                <ProtectedRoute requiredRole="company">
                  <CompanySettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/products" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_products">
                  <CompanyProducts />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/ingredients" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_ingredients">
                  <CompanyIngredients />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/suppliers" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_suppliers">
                  <CompanySuppliers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/inventory" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_inventory">
                  <CompanyInventory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/purchase-orders" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_purchase_orders">
                  <CompanyPurchaseOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/purchase-orders/new" 
              element={<Navigate to="/company/purchase-orders?tab=new" replace />}
            />
            <Route 
              path="/company/starters" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_starters">
                  <CompanyStarters />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/recipes" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_recipes">
                  <CompanyRecipes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/production" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_production">
                  <CompanyProductions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/batch-optimizer" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_batch_optimizer">
                  <CompanyBatchOptimization />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/cost-calculator" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_cost_calculator">
                  <CostCalculator />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/bakers-calculator" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_baker_calculator">
                  <BakersCalculator />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/webstore/settings" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_store_settings">
                  <WebstoreSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/webstore/customers" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_store_customers">
                  <WebstoreCustomers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/webstore/orders" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_store_orders">
                  <WebstoreOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/webstore/coupons" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_store_settings">
                  <WebstoreCoupons />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/webstore/messages" 
              element={
                <ProtectedRoute requiredRole="company" permKey="access_messages">
                  <WebstoreMessages />
                </ProtectedRoute>
              } 
            />
            {/* Public retail store — accessible at /store on any subdomain or custom domain */}
            <Route path="/store" element={<PublicStore />} />
            <Route 
              path="/company/tomorrows-bake" 
              element={
                <ProtectedRoute requiredRole="company">
                  <TomorrowsBake />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/todays-delivery" 
              element={
                <ProtectedRoute requiredRole="company">
                  <TodaysDelivery />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/support" 
              element={
                <ProtectedRoute requiredRole="company">
                  <SupportTickets />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/support/new" 
              element={
                <ProtectedRoute requiredRole="company">
                  <NewTicket />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/company/support/:id" 
              element={
                <ProtectedRoute requiredRole="company">
                  <TicketDetail />
                </ProtectedRoute>
              } 
            />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        </MaintenanceGate>
        <CookieConsent />
      </Router>
    </AuthProvider>
    </SiteConfigProvider>
  );
}

export default App;
